import React, { useState, useCallback, useEffect, useMemo } from 'react'
import {
    Box,
    TextField,
    Paper,
    Typography,
    IconButton,
    InputAdornment,
    Skeleton,
    CardActionArea,
    Tooltip,
    Pagination,
    Divider,
    Chip,
    Fade,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBagOutlined'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import { NumericFormat } from 'react-number-format'
import { debounce } from 'lodash'

/** TYPES */
import type { Product, Category } from '@/types/product'
import { CartItem } from '@/types/pos'

/** SERVICES */
import { fetchProducts, fetchCategories } from '@/hooks/product'

interface POSProductsProps {
    addItemToCart: (items: CartItem[]) => void
}

const POSProducts = ({ addItemToCart }: POSProductsProps) => {
    const [products, setProducts] = useState<Product[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [searchValue, setSearchValue] = useState<string>('')
    const [searchTerm, setSearchTerm] = useState('')
    const [page, setPage] = useState<number>(1)
    const [totalCount, setTotalCount] = useState<number>(0)
    const [categories, setCategories] = useState<Category[]>([])
    const [selectedCategoriesIds, setSelectedCategoriesIds] = useState<string[]>([])

    const pageSize = 20

    const debouncedSearch = useMemo(() =>
        debounce((value: string) => {
            setSearchTerm(value)
            setPage(1)
        }, 500)
    , [])

    useEffect(() => {
        debouncedSearch(searchValue)
        return () => debouncedSearch.cancel()
    }, [searchValue, debouncedSearch])

    const loadCategories = useCallback(async () => {
        try {
            const fetchedCategories = await fetchCategories()
            setCategories(fetchedCategories.categories || [])
        } catch (error) {
            console.error('Failed to fetch categories:', error)
        }
    }, [])

    useEffect(() => {
        loadCategories()
    }, [loadCategories])

    const loadProducts = useCallback(async () => {
        setLoading(true)
        try {
            const response = await fetchProducts({
                limit: pageSize,
                page: page,
                search: searchTerm,
                categoryIds: selectedCategoriesIds,
            })
            setProducts(response.products || [])
            setTotalCount(response.total || 0)
        } catch (error) {
            console.error('Failed to fetch products:', error)
        } finally {
            setTimeout(() => setLoading(false), 150)
        }
    }, [page, searchTerm, selectedCategoriesIds])

    useEffect(() => {
        loadProducts()
    }, [loadProducts])

    const totalPages = Math.ceil(totalCount / pageSize)

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* TOP ROW: Search and Categories Combined */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3, 
                bgcolor: 'background.paper',
                p: 1.5,
                borderRadius: 2,
                borderBottom: 1,
                borderColor: 'divider',
            }}>
                <TextField
                    placeholder="Хайх..."
                    variant="outlined"
                    size="small"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    sx={{ width: 260, flexShrink: 0 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
                
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <Box sx={{ 
                    display: 'flex', 
                    overflowX: 'auto', 
                    gap: 1, 
                    flexGrow: 1,
                    py: 0.5,
                    '&::-webkit-scrollbar': { display: 'none' }, // Clean look
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                }}>
                    <Chip 
                        label="Бүгд"
                        size="small"
                        onClick={() => setSelectedCategoriesIds([])}
                        color={selectedCategoriesIds.length === 0 ? 'primary' : 'default'}
                        variant={selectedCategoriesIds.length === 0 ? 'filled' : 'outlined'}
                    />
                    {categories.map((category) => {
                        const isSelected = selectedCategoriesIds.includes(category.id)
                        return (
                            <Chip
                                key={category.id}
                                label={category.name}
                                size="small"
                                color={isSelected ? 'primary' : 'default'}
                                variant={isSelected ? 'filled' : 'outlined'}
                                onClick={() => {
                                    setPage(1)
                                    setSelectedCategoriesIds(prev => 
                                        isSelected ? prev.filter(id => id !== category.id) : [...prev, category.id]
                                    )
                                }}
                                sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                            />
                        )
                    })}
                </Box>
            </Box>

            {/* MIDDLE: Product Grid (Scrollable and fills space) */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 , padding: 2}}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: 2,
                        alignItems: 'start', // IMPORTANT: Prevents tall cards
                    }}
                >
                    {loading ? (
                        Array.from(new Array(10)).map((_, index) => (
                            <ProductSkeleton key={`skeleton-${index}`} />
                        ))
                    ) : (
                        products.map((product, index) => (
                            <Fade in={!loading} key={product.id} timeout={200 + index * 20}>
                                <Box>
                                    <ProductItem product={product} onAddToCart={addItemToCart} />
                                </Box>
                            </Fade>
                        ))
                    )}
                </Box>
            </Box>

            {/* BOTTOM: Pagination (Always at the bottom) */}
            {totalPages > 1 && (
                <Box sx={{ 
                    pt: 2, 
                    pb: 1, 
                    borderTop: '1px solid', 
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: 'background.default'
                }}>
                    <Pagination 
                        count={totalPages} 
                        page={page} 
                        onChange={(_, v) => setPage(v)} 
                        color="primary" 
                        shape="rounded"
                        size="medium"
                    />
                </Box>
            )}
        </Box>
    )
}

const ProductItem = ({ product, onAddToCart }: { product: Product; onAddToCart: (items: CartItem[]) => void }) => {
    const handleAdd = () => {
        onAddToCart([{
            type: 'product',
            name: product.name || product.title || 'Бүтээгдэхүүн',
            label: product.title,
            id: product.id,
            total: product.price ?? 0,
            quantity: 1,
            price: product.price ?? 0,
        }])
    }

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    borderColor: 'rgba(0,0,0,0.12)',
                },
            }}
        >
            <CardActionArea onClick={handleAdd}>
                <Box sx={{
                    height: 120, // Slightly more compact
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'grey.50',
                }}>
                    {product.bannerImage ? (
                        <Box component="img" src={product.bannerImage} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <ShoppingBagIcon sx={{ fontSize: 32, color: 'grey.300' }} />
                    )}
                </Box>

                <Box sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                        ID: {product.id?.slice(-6).toUpperCase()}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, height: '2.8em', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.2 }}>
                        {product.name || product.title}
                    </Typography>
                </Box>
            </CardActionArea>

            <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 700 }}>
                    <NumericFormat value={product.price} displayType={'text'} thousandSeparator={true} suffix={' ₮'} />
                </Typography>
                <IconButton 
                    size="small" 
                    onClick={handleAdd}
                    sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, width: 28, height: 28 }}
                >
                    <AddShoppingCartIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        </Paper>
    )
}

const ProductSkeleton = () => (
    <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Skeleton variant="rectangular" height={120} />
        <Box sx={{ p: 1.5 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" height={20} width="90%" />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="circular" width={24} height={24} />
            </Box>
        </Box>
    </Paper>
)

export default POSProducts